/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";

import { IModelComponentProps } from "../../shared/IModelComponentProps";
import { InputNumber } from "../../components/fields/InputNumber";
import { TModel_FPI_MSP_2020_05_Handler } from "./Model_FPI_MSP_2020_05_Handler";
import { OutputNumber } from "../../components/fields/OutputNumber";
import { InputOptions } from "../../components/fields/InputOptions";
import { PageSection } from "../../components/PageSection";
import { RTC } from "../../components/RTC";
import { InputGroup } from "../../components/InputGroup";
import { PageContents } from "../../components/PageContents";
import { PageHeader } from "../../components/PageHeader";
import { PageSidebar } from "../../components/PageSidebar";
import { SidebarSection } from "../../components/SidebarSection";
import { OutputGroup } from "../../components/OutputGroup";
import { SidebarPanel } from "../../components/SidebarPanel";
import { OutputCustom } from "../../components/fields/OutputCustom";
import { Button } from "../../components/Button";
import { ButtonGroup } from "../../components/ButtonGroup";
import { ButtonSave } from "../../components/ButtonSave";
import { ButtonOpen } from "../../components/ButtonOpen";
import { useModelActions } from "../../shared/hooks/useModelActions";
import { InputText } from "../../components/fields/InputText";
import { OutputResultBlock } from "../../components/fields/OutputResultBlock";
import { sanitizeName } from "../../shared/helpers";
import { Model_FPI_MSP_2020_05_Component_ExportXLSX } from "./Model_FPI_MSP_2020_05_Export";

/**
 * API Endpoint to download model as XLSX
 */
export const API_URL_MODEL_TO_XLSX = "https://nn-msp-fpi.prod.vstecb.cz​/model-to-xlsx-v1";

/**
 * API Endpoint to download model as XLSX
 */
export const API_URL_MODEL_TO_DOCX = "https://nn-msp-fpi.prod.vstecb.cz​/model-to-docx-v1";

/**
 * Model component
 *
 * @param props Component props
 */
export function Model_FPI_MSP_2020_05_Component(props: IModelComponentProps<TModel_FPI_MSP_2020_05_Handler>) {

	const getFilenameBase = useMemo(() => () => {

		const customName = props.handler.inputs.jmenoPodniku.get() || "";
		return props.spec.id + ( customName ? "-" + sanitizeName(customName) : "" );

	}, [ props.spec, props.handler ]);

	const { doSave, handleOpen, doReset } = useModelActions(props.spec, props.handler, getFilenameBase);

	const doExportXLSX = useMemo(() => () => {

		return {
			url: API_URL_MODEL_TO_XLSX,
			data: {
				filename: getFilenameBase() + ".xlsx",
				sheet_data: Model_FPI_MSP_2020_05_Component_ExportXLSX(props.spec, props.handler)
			}
		};

	}, [ props.spec, props.handler ]);

	return <>
		<PageContents withSidebar={true}>
			<PageHeader
				icon={props.spec.icon}
				title={props.spec.titleLong}
				subtitle={`Verze ${props.spec.version}`}
			/>
			<PageSection>
				<RTC>
					<p></p>
				</RTC>
			</PageSection>
			<PageSection>
				<InputGroup>
					<InputText
						input={props.handler.inputs.jmenoPodniku}
						description="Nepovinné."
						size={50}
					/>
				</InputGroup>

				<h4><i className="mdi mdi-file-table" /> <span>Účetní rozvaha - AKTIVA</span></h4>
				<InputGroup>
					<InputNumber
						input={props.handler.inputs.aktivaCelkem}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.dlouhodobyMajetek}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.dlouhodobyFinMajetek}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.obeznaAktiva}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.zasoby}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.kratkodobePohledavky}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.kratkodobyFinMajetek}
						step={100}
					/>
				</InputGroup>

				<h4><i className="mdi mdi-file-table-outline" /> <span>Účetní rozvaha - PASIVA</span></h4>
				<InputGroup>
					<InputNumber
						input={props.handler.inputs.vlastniKapital}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.ciziZdroje}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.kratkodobeZavazky}
						step={100}
					/>
				</InputGroup>

				<h4><i className="mdi mdi-file-chart" /> <span>Výkaz zisku a ztráty (VZZ)</span></h4>
				<InputGroup>
					<InputNumber
						input={props.handler.inputs.trzbyProdejVyrobkySluzby}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.trzbyProdejZbozi}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.nakladyNaProdaneZbozi}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.spotrebaMaterialu}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.sluzby}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.osobniNaklady}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.odpisy}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.trzbyOstatniProvozni}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.trzbyFinMajetekPodily}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.celkovyNakladovyUrok}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.ebt}
						step={100}
					/>

					<InputNumber
						input={props.handler.inputs.cistyZisk}
						step={100}
					/>

				</InputGroup>

				<OutputCustom handler={props.handler} render={() => {
					if (props.handler.inputs.cistyZisk.get() < 0 && props.handler.inputs.vlastniKapital.get() < 0) {
						return <RTC>
							<p className="error margin-top-10">Záporný čistý zisk a zároveň záporný vlastní kapitál nepředstavují validní hodnoty.</p>
						</RTC>;
					} else {
						return null;
					}
				}} />

				<h4><i className="mdi mdi-clipboard-text-outline" /> <span>Doplňující informace</span></h4>

				<InputGroup>
					<InputOptions
						input={props.handler.inputs.NACE}
						descriptionHTML={`Více informací na <a href="http://www.nace.cz/." target="_blank">http://www.nace.cz/</a>.`}
					/>

					<InputOptions
						input={props.handler.inputs.kraj}
					/>
				</InputGroup>
			</PageSection>
		</PageContents>
		<PageSidebar>
			<SidebarPanel disableShrink={true}>
				<ButtonGroup>
					<ButtonSave icon="content-save" label="Uložit" style="primary" doSave={doSave} />
					<ButtonOpen icon="folder-open" label="Načíst" style="primary" onOpen={handleOpen} accept="application/json" />
					<ButtonSave icon="file-excel" title="Exportovat do XLSX" doSave={doExportXLSX} />
					{/*<Button icon="file-word" title="Exportovat do DOCX" disabled={true} /> */}
					<span className="spacer" />
					<Button icon="reload" title="Resetovat hodnoty na původní" onClick={doReset} />
				</ButtonGroup>
			</SidebarPanel>
			<SidebarPanel grow={true}>
				<h3>Výstupy modelu</h3>

				<SidebarSection>
					<h4>Ukazatele rentability</h4>
					<OutputGroup>
						<OutputNumber
							output={props.handler.outputs.roe}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.roa}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.ros}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>
					</OutputGroup>

					<h4>Ukazatele aktivity</h4>
					<OutputGroup>
						<OutputNumber
							output={props.handler.outputs.obratZasob}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.obratDlouhodobehoMajetku}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.obratMajetku}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.dobaObratkyZasob}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.dobaObratkyDlouhodobehoMajetku}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.dobaObratkyMajetku}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>
					</OutputGroup>

					<h4>Ukazatele zadluženosti</h4>
					<OutputGroup>
						<OutputNumber
							output={props.handler.outputs.ciziKapitalCelkovyKapital}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.urokoveKryti1}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.urokoveKryti2}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>
					</OutputGroup>

					<h4>Ukazatele likvidity</h4>
					<OutputGroup>
						<OutputNumber
							output={props.handler.outputs.likvidita1}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.likvidita2}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>

						<OutputNumber
							output={props.handler.outputs.likvidita3}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>
					</OutputGroup>

					<h4>EVA Equity</h4>
					<OutputGroup>
						<OutputNumber
							output={props.handler.outputs.evaEquity}
							updateOnModelChange={true}
							renderMore={(state) => <OutputResultBlock state={state} handler={props.handler} />}
						/>
					</OutputGroup>
				</SidebarSection>
			</SidebarPanel>
		</PageSidebar>
	</>;

}
